import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Phone, MapPin, Eye, EyeOff, Loader2, Building2, FileText, Globe, DollarSign, X, ArrowLeft, CheckCircle2, Upload, CreditCard, Palmtree, ShieldCheck, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, useLanguage, API } from '../App';
import Navbar from '../components/Navbar';
import axios from '../lib/axios';
import { SafariDropdown } from '../components/ui/safari-dropdown';
import { Turnstile } from '@marsidev/react-turnstile';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

// Forgot Password Modal Component
const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Always show success to prevent email enumeration
      setSent(true);
      toast.success('If an account exists with this email, you will receive reset instructions.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSent(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Reset Password</h3>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-secondary transition-colors"
            data-testid="close-forgot-password-modal"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {sent ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-500/10 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <h4 className="text-lg font-semibold text-foreground mb-2">Check Your Email</h4>
              <p className="text-muted-foreground text-sm mb-6">
                If an account exists with <span className="font-medium text-foreground">{email}</span>, 
                you will receive password reset instructions shortly.
              </p>
              <button
                onClick={handleClose}
                className="w-full py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors"
                data-testid="forgot-password-done-btn"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <p className="text-muted-foreground text-sm mb-6">
                Enter your email address and we'll send you instructions to reset your password.
              </p>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full py-3 pl-12 pr-4 bg-secondary border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 outline-none"
                    required
                    autoFocus
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="forgot-password-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>

              <button
                type="button"
                onClick={handleClose}
                className="w-full mt-3 py-3 text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Social Login Icon Components
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const AppleIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
  </svg>
);

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register, loginWithToken } = useAuth();
  const { t, language } = useLanguage();
  
  // Show register form by default when accessed via /register route
  const [isLogin, setIsLogin] = useState(!location.pathname.includes('register'));
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accountType, setAccountType] = useState('private');
  
  // Registration success state for visual feedback
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  // Check for registered query param (for login page hint)
  const justRegistered = searchParams.get('registered') === 'true';
  const registeredEmailParam = searchParams.get('email') || '';
  
  // Processing guard for OAuth callbacks (prevents React 18 Strict Mode double execution)
  const isProcessingGoogleRef = useRef(false);
  const isProcessingFacebookRef = useRef(false);
  
  const [socialConfig, setSocialConfig] = useState({ 
    google_enabled: true, 
    facebook_enabled: false, 
    apple_enabled: false,
    facebook_app_id: null,
    apple_client_id: null
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [islands, setIslands] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Business document upload state
  const [kvkDocument, setKvkDocument] = useState(null);
  const [idDocument, setIdDocument] = useState(null);
  const [idType, setIdType] = useState('id_card');
  const [uploadingDocs, setUploadingDocs] = useState(false);
  
  // Turnstile CAPTCHA state
  const [turnstileToken, setTurnstileToken] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    location: '',
    business_name: '',
    kvk_number: '',
    preferred_language: language,
    preferred_currency: 'USD',
  });
  
  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
    passwordsMatch: false
  });
  
  // Validate password on change
  useEffect(() => {
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;
    
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      passwordsMatch: password.length > 0 && password === confirmPassword
    });
  }, [formData.password, formData.confirmPassword]);

  // Fetch social login config
  useEffect(() => {
    const fetchSocialConfig = async () => {
      try {
        const res = await axios.get(`${API}/auth/social-login-config`);
        setSocialConfig(res.data);
      } catch (error) {
        console.log('Using default social config');
      }
    };
    fetchSocialConfig();
  }, []);

  // Handle Google OAuth callback
  useEffect(() => {
    const hash = window.location.hash;
    
    // Early exit if no session_id in hash
    if (!hash || !hash.includes('session_id=')) {
      return;
    }
    
    // Processing guard - prevent double execution (React 18 Strict Mode)
    if (isProcessingGoogleRef.current) {
      return;
    }
    
    const sessionId = hash.split('session_id=')[1]?.split('&')[0];
    if (!sessionId) {
      return;
    }
    
    // Set guard immediately
    isProcessingGoogleRef.current = true;
    
    // Clear the hash from URL BEFORE making API call (prevents re-triggering)
    window.history.replaceState(null, '', window.location.pathname);
    
    const processGoogleCallback = async () => {
      setGoogleLoading(true);
      try {
        const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        const { token, user } = response.data;
        
        // Login with the token (this sets skipNextFetch in AuthContext)
        loginWithToken(token, user);
        toast.success(`Welcome${user.name ? ', ' + user.name : ''}!`);
        
        // Small delay to ensure state propagates before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Check for post-auth redirect (e.g., from start-selling page)
        const postAuthRedirect = localStorage.getItem('postAuthRedirect');
        if (postAuthRedirect) {
          localStorage.removeItem('postAuthRedirect');
          navigate(postAuthRedirect, { replace: true });
        } else {
          // Default redirect
          const from = location.state?.from?.pathname || '/';
          navigate(from, { replace: true });
        }
      } catch (error) {
        console.error('Google auth error:', error);
        // Only show error if it's a real server error, not a duplicate request
        if (error.response?.status >= 400) {
          toast.error('Google sign-in failed. Please try again.');
        }
      } finally {
        setGoogleLoading(false);
        // Reset guard after a delay to allow for proper cleanup
        setTimeout(() => {
          isProcessingGoogleRef.current = false;
        }, 1000);
      }
    };
    
    processGoogleCallback();
  }, [loginWithToken, navigate, location.state]);

  // Handle Facebook OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const provider = searchParams.get('provider');
    
    // Early exit if not a Facebook callback
    if (!code || !(provider === 'facebook' || location.pathname.includes('facebook'))) {
      return;
    }
    
    // Processing guard
    if (isProcessingFacebookRef.current) {
      return;
    }
    isProcessingFacebookRef.current = true;
    
    // Clear URL params BEFORE making API call
    window.history.replaceState(null, '', '/auth');
    
    const processFacebookCallback = async () => {
      setFacebookLoading(true);
      try {
        const response = await axios.post(`${API}/auth/facebook/callback`, { code, state });
        const { token, user } = response.data;
        
        loginWithToken(token, user);
        toast.success(`Welcome${user.name ? ', ' + user.name : ''}!`);
        
        // Small delay to ensure state propagates before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
      } catch (error) {
        console.error('Facebook auth error:', error);
        if (error.response?.status >= 400) {
          toast.error('Facebook sign-in failed. Please try again.');
        }
      } finally {
        setFacebookLoading(false);
        setTimeout(() => {
          isProcessingFacebookRef.current = false;
        }, 1000);
      }
    };
    
    processFacebookCallback();
  }, [searchParams, loginWithToken, navigate, location]);

  // Handle Apple OAuth callback (Apple uses form_post, so we handle it differently)
  useEffect(() => {
    const processAppleCallback = async () => {
      const code = searchParams.get('code');
      const idToken = searchParams.get('id_token');
      const state = searchParams.get('state');
      const provider = searchParams.get('provider');
      
      if (code && (provider === 'apple' || location.pathname.includes('apple'))) {
        setAppleLoading(true);
        try {
          const response = await axios.post(`${API}/auth/apple/callback`, { 
            code, 
            id_token: idToken,
            state 
          });
          const { token, user } = response.data;
          
          // Clear URL params
          window.history.replaceState(null, '', '/auth');
          
          loginWithToken(token, user);
          toast.success(`Welcome${user.name ? ', ' + user.name : ''}!`);
          
          const from = location.state?.from?.pathname || '/';
          navigate(from, { replace: true });
        } catch (error) {
          console.error('Apple auth error:', error);
          toast.error('Apple sign-in failed. Please try again.');
          window.history.replaceState(null, '', '/auth');
        } finally {
          setAppleLoading(false);
        }
      }
    };
    
    processAppleCallback();
  }, [searchParams, loginWithToken, navigate, location]);

  // Fetch active islands for location dropdown
  useEffect(() => {
    const fetchIslands = async () => {
      try {
        const response = await axios.get(`${API}/islands`);
        setIslands(response.data || []);
      } catch (error) {
        console.error('Failed to fetch islands');
      }
    };
    fetchIslands();
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth';
    const appName = encodeURIComponent('Your Buy Site');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${appName}`;
  };

  const handleFacebookLogin = async () => {
    if (!socialConfig.facebook_enabled) {
      toast.error('Facebook login is not configured');
      return;
    }
    setFacebookLoading(true);
    try {
      const response = await axios.get(`${API}/auth/facebook/login`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Facebook login error:', error);
      toast.error('Failed to initiate Facebook login');
      setFacebookLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    if (!socialConfig.apple_enabled) {
      toast.error('Apple Sign In is not configured');
      return;
    }
    setAppleLoading(true);
    try {
      const response = await axios.get(`${API}/auth/apple/login`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Apple login error:', error);
      toast.error('Failed to initiate Apple Sign In');
      setAppleLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        if (!formData.name) {
          toast.error('Name is required');
          setLoading(false);
          return;
        }
        
        // Terms and conditions validation
        if (!acceptedTerms) {
          toast.error('Please accept the Terms & Conditions to continue');
          setLoading(false);
          return;
        }
        
        if (accountType === 'business') {
          if (!formData.business_name) {
            toast.error('Business name is required');
            setLoading(false);
            return;
          }
          if (!formData.kvk_number) {
            toast.error('KVK number is required for business accounts');
            setLoading(false);
            return;
          }
          // Validate business documents
          if (!kvkDocument) {
            toast.error('KVK overview document is required for business accounts');
            setLoading(false);
            return;
          }
          if (!idDocument) {
            toast.error('ID document (ID card or passport) is required for business accounts');
            setLoading(false);
            return;
          }
        }
        
        // Password validation for registration
        if (!passwordValidation.minLength) {
          toast.error('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        if (!passwordValidation.hasUppercase || !passwordValidation.hasLowercase) {
          toast.error('Password must contain uppercase and lowercase letters');
          setLoading(false);
          return;
        }
        if (!passwordValidation.hasNumber) {
          toast.error('Password must contain at least one number');
          setLoading(false);
          return;
        }
        if (!passwordValidation.passwordsMatch) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        
        // Verify CAPTCHA for registration
        if (TURNSTILE_SITE_KEY && !turnstileToken) {
          toast.error('Please complete the CAPTCHA verification');
          setLoading(false);
          return;
        }
        
        const result = await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone || null,
          location: formData.location || null,
          account_type: accountType,
          business_name: accountType === 'business' ? formData.business_name : null,
          kvk_number: accountType === 'business' ? formData.kvk_number : null,
          preferred_language: formData.preferred_language,
          preferred_currency: formData.preferred_currency,
          turnstile_token: turnstileToken,
        });
        
        // Upload business documents FIRST if this is a business account
        // This must happen before email verification check, otherwise docs won't be uploaded
        if (accountType === 'business' && kvkDocument && idDocument) {
          setUploadingDocs(true);
          try {
            const docFormData = new FormData();
            docFormData.append('kvk_document', kvkDocument);
            docFormData.append('id_document', idDocument);
            docFormData.append('id_type', idType);
            
            // If email verification is required, use the token-based upload
            if (result?.email_verification_required && result?.upload_token && result?.user_id) {
              docFormData.append('upload_token', result.upload_token);
              docFormData.append('user_id', result.user_id);
              await axios.post(`${API}/upload/business-documents-with-token`, docFormData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });
            } else {
              // Normal upload for already-authenticated users
              await axios.post(`${API}/upload/business-documents`, docFormData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });
            }
            // Don't show toast here - will show combined message below
          } catch (docError) {
            console.error('Document upload failed:', docError);
            toast.error('Account created but document upload failed. Please upload documents later from your profile.');
          } finally {
            setUploadingDocs(false);
          }
        }
        
        // Check if email verification is required
        if (result?.email_verification_required) {
          // Set success state to show visual confirmation
          setRegistrationSuccess(true);
          setRegisteredEmail(formData.email);
          setLoading(false);
          
          // Track referral signup conversion if user came via a referral link
          const referralCode = sessionStorage.getItem('referral_code_for_signup');
          if (referralCode) {
            try {
              await axios.post(`${API}/referrals/track-signup-conversion?ref_code=${referralCode}`);
              sessionStorage.removeItem('referral_code_for_signup');
            } catch (refError) {
              console.error('Failed to track referral conversion:', refError);
            }
          }
          
          // Timed redirect to login page after 2.5 seconds
          setTimeout(() => {
            // Use window.location for a clean redirect that ensures the banner shows
            window.location.href = `/auth?registered=true&email=${encodeURIComponent(formData.email)}`;
          }, 2500);
          
          return;
        }
        
        toast.success('Account created successfully!');
        
        // Track referral signup conversion if user came via a referral link
        const referralCode = sessionStorage.getItem('referral_code_for_signup');
        if (referralCode) {
          try {
            await axios.post(`${API}/referrals/track-signup-conversion?ref_code=${referralCode}`);
            sessionStorage.removeItem('referral_code_for_signup');
          } catch (refError) {
            console.error('Failed to track referral conversion:', refError);
          }
        }
      }
      
      // Small delay to ensure state propagates before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || (isLogin ? 'Invalid credentials' : 'Registration failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="auth-page">
      <Navbar />

      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-6 mx-auto">
              {isLogin ? <Lock className="w-8 h-8 text-white" /> : <User className="w-8 h-8 text-white" />}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              {isLogin ? t('sign_in') : t('sign_up')}
            </h1>
            <p className="text-muted-foreground text-lg">
              {isLogin
                ? 'Welcome back! Sign in to continue'
                : 'Join the ABC islands marketplace'}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-card rounded-3xl p-6 md:p-8 shadow-xl border border-border backdrop-blur-sm">
            
            {/* Registration Success State */}
            {registrationSuccess && (
              <div className="text-center py-8" data-testid="registration-success">
                <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  {language === 'nl' ? 'Account aangemaakt!' : language === 'es' ? '¡Cuenta creada!' : 'Account Created!'}
                </h2>
                <p className="text-muted-foreground mb-4">
                  {language === 'nl' 
                    ? 'We hebben een verificatielink gestuurd naar:' 
                    : language === 'es' 
                    ? 'Hemos enviado un enlace de verificación a:'
                    : 'We sent a verification link to:'}
                </p>
                <p className="text-lg font-semibold text-primary mb-6">{registeredEmail}</p>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>
                    {language === 'nl' 
                      ? 'Je wordt doorgestuurd naar de login-pagina...' 
                      : language === 'es' 
                      ? 'Redirigiendo a la página de inicio de sesión...'
                      : 'Redirecting to login page...'}
                  </span>
                </div>
              </div>
            )}
            
            {/* Login Page - Just Registered Banner */}
            {isLogin && justRegistered && !registrationSuccess && (
              <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30" data-testid="verify-email-banner">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-600 dark:text-green-400 mb-1">
                      {language === 'nl' ? 'Bijna klaar!' : language === 'es' ? '¡Casi listo!' : 'Almost there!'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {registeredEmailParam ? (
                        <>
                          {language === 'nl' 
                            ? `Check je inbox voor de verificatielink. We hebben een e-mail gestuurd naar ` 
                            : language === 'es' 
                            ? `Revisa tu bandeja de entrada para el enlace de verificación. Enviamos un correo a `
                            : `Check your inbox for the verification link. We sent an email to `}
                          <span className="font-medium text-foreground">{decodeURIComponent(registeredEmailParam)}</span>
                        </>
                      ) : (
                        language === 'nl' 
                          ? 'Check je inbox voor de verificatielink voordat je inlogt.' 
                          : language === 'es' 
                          ? 'Revisa tu bandeja de entrada para el enlace de verificación antes de iniciar sesión.'
                          : 'Check your inbox for the verification link before logging in.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Account Type Toggle (Register only) */}
            {!isLogin && !registrationSuccess && (
              <div className="mb-10">
                <label className="form-label">{t('account_type')}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setAccountType('private')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      accountType === 'private'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="account-type-private"
                  >
                    <User className={`w-6 h-6 mx-auto mb-2 ${accountType === 'private' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className={`font-medium ${accountType === 'private' ? 'text-primary' : 'text-foreground'}`}>
                      {t('private_account')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">For individuals</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountType('business')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      accountType === 'business'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="account-type-business"
                  >
                    <Building2 className={`w-6 h-6 mx-auto mb-2 ${accountType === 'business' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className={`font-medium ${accountType === 'business' ? 'text-primary' : 'text-foreground'}`}>
                      {t('business_account')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">KVK required</p>
                  </button>
                </div>
              </div>
            )}

            {!registrationSuccess && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label className="form-label">{t('name')} *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="form-input pl-12"
                      required={!isLogin}
                      data-testid="auth-name-input"
                    />
                  </div>
                </div>
              )}

              {/* Business Fields */}
              {!isLogin && accountType === 'business' && (
                <>
                  <div>
                    <label className="form-label">{t('business_name')} *</label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="text"
                        name="business_name"
                        value={formData.business_name}
                        onChange={handleChange}
                        placeholder="Your Business Name"
                        className="form-input pl-12"
                        required={accountType === 'business'}
                        data-testid="auth-business-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">{t('kvk_number')} *</label>
                    <div className="relative">
                      <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="text"
                        name="kvk_number"
                        value={formData.kvk_number}
                        onChange={handleChange}
                        placeholder="12345678"
                        className="form-input pl-12"
                        required={accountType === 'business'}
                        data-testid="auth-kvk-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Chamber of Commerce registration number
                    </p>
                  </div>
                  
                  {/* Business Document Uploads */}
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Upload className="w-5 h-5 text-amber-500" />
                      {language === 'nl' ? 'Verificatiedocumenten' : language === 'es' ? 'Documentos de Verificación' : 'Verification Documents'}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-4">
                      {language === 'nl' 
                        ? 'Upload uw KVK-uittreksel en een geldig identiteitsbewijs zodat wij kunnen verifiëren dat u de eigenaar bent van het bedrijf.'
                        : language === 'es'
                        ? 'Suba su extracto de la Cámara de Comercio y un documento de identidad válido para verificar que usted es el propietario de la empresa.'
                        : 'Upload your Chamber of Commerce extract and a valid ID document so we can verify you are the owner of the business.'}
                    </p>
                    
                    {/* KVK Document Upload */}
                    <div className="mb-4">
                      <label className="form-label text-sm">
                        {language === 'nl' ? 'KVK-uittreksel' : language === 'es' ? 'Extracto de Cámara de Comercio' : 'KVK Overview'} *
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setKvkDocument(e.target.files[0])}
                          className="hidden"
                          id="kvk-document-upload"
                          data-testid="kvk-document-input"
                        />
                        <label
                          htmlFor="kvk-document-upload"
                          className={`flex items-center justify-center gap-2 w-full py-3 px-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                            kvkDocument 
                              ? 'border-green-500 bg-green-500/10 text-green-600' 
                              : 'border-border hover:border-primary hover:bg-primary/5 text-muted-foreground'
                          }`}
                        >
                          {kvkDocument ? (
                            <>
                              <CheckCircle2 className="w-5 h-5" />
                              <span className="truncate max-w-[200px]">{kvkDocument.name}</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-5 h-5" />
                              <span>{language === 'nl' ? 'Upload KVK-uittreksel' : language === 'es' ? 'Subir extracto' : 'Upload KVK document'}</span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                    
                    {/* ID Type Selection */}
                    <div className="mb-4">
                      <label className="form-label text-sm">
                        {language === 'nl' ? 'Type identiteitsbewijs' : language === 'es' ? 'Tipo de documento' : 'ID Document Type'} *
                      </label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setIdType('id_card')}
                          className={`flex-1 py-2 px-4 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                            idType === 'id_card' 
                              ? 'border-primary bg-primary/10 text-primary' 
                              : 'border-border text-muted-foreground hover:border-primary/50'
                          }`}
                        >
                          <CreditCard className="w-4 h-4" />
                          {language === 'nl' ? 'ID-kaart' : language === 'es' ? 'Tarjeta ID' : 'ID Card'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIdType('passport')}
                          className={`flex-1 py-2 px-4 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                            idType === 'passport' 
                              ? 'border-primary bg-primary/10 text-primary' 
                              : 'border-border text-muted-foreground hover:border-primary/50'
                          }`}
                        >
                          <FileText className="w-4 h-4" />
                          {language === 'nl' ? 'Paspoort' : language === 'es' ? 'Pasaporte' : 'Passport'}
                        </button>
                      </div>
                    </div>
                    
                    {/* ID Document Upload */}
                    <div>
                      <label className="form-label text-sm">
                        {idType === 'id_card' 
                          ? (language === 'nl' ? 'ID-kaart (voor- en achterkant)' : language === 'es' ? 'Tarjeta ID (frente y reverso)' : 'ID Card (front & back)')
                          : (language === 'nl' ? 'Paspoort (gegevenspagina)' : language === 'es' ? 'Pasaporte (página de datos)' : 'Passport (data page)')
                        } *
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setIdDocument(e.target.files[0])}
                          className="hidden"
                          id="id-document-upload"
                          data-testid="id-document-input"
                        />
                        <label
                          htmlFor="id-document-upload"
                          className={`flex items-center justify-center gap-2 w-full py-3 px-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                            idDocument 
                              ? 'border-green-500 bg-green-500/10 text-green-600' 
                              : 'border-border hover:border-primary hover:bg-primary/5 text-muted-foreground'
                          }`}
                        >
                          {idDocument ? (
                            <>
                              <CheckCircle2 className="w-5 h-5" />
                              <span className="truncate max-w-[200px]">{idDocument.name}</span>
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-5 h-5" />
                              <span>{language === 'nl' ? 'Upload identiteitsbewijs' : language === 'es' ? 'Subir documento' : 'Upload ID document'}</span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                    
                    <p className="text-xs text-amber-600 mt-3">
                      {language === 'nl' 
                        ? '⚠️ Uw documenten worden veilig opgeslagen en alleen bekeken door ons verificatieteam.'
                        : language === 'es'
                        ? '⚠️ Sus documentos se almacenan de forma segura y solo los revisa nuestro equipo de verificación.'
                        : '⚠️ Your documents are stored securely and only reviewed by our verification team.'}
                    </p>
                  </div>
                </>
              )}

              <div>
                <label className="form-label">{t('email')} *</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="form-input pl-12"
                    required
                    data-testid="auth-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">{t('password')} *</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={isLogin ? "••••••••" : "Create a strong password"}
                    className={`form-input pl-12 pr-12 ${!isLogin && formData.password && !passwordValidation.minLength ? 'border-red-500 focus:border-red-500' : ''}`}
                    required
                    minLength={isLogin ? 1 : 8}
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-btn"
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Requirements - Only show on registration */}
                {!isLogin && formData.password && (
                  <div className="mt-3 p-3 bg-secondary/50 rounded-xl border border-border">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Password requirements:</p>
                    <div className="grid grid-cols-2 gap-1.5 text-xs">
                      <div className={`flex items-center gap-1.5 ${passwordValidation.minLength ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordValidation.minLength ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                        <span>8+ characters</span>
                      </div>
                      <div className={`flex items-center gap-1.5 ${passwordValidation.hasUppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordValidation.hasUppercase ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                        <span>Uppercase letter</span>
                      </div>
                      <div className={`flex items-center gap-1.5 ${passwordValidation.hasLowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordValidation.hasLowercase ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                        <span>Lowercase letter</span>
                      </div>
                      <div className={`flex items-center gap-1.5 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordValidation.hasNumber ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                        <span>Number</span>
                      </div>
                      <div className={`flex items-center gap-1.5 ${passwordValidation.hasSpecial ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordValidation.hasSpecial ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                        <span>Special character</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Forgot Password Link - Only show on login */}
                {isLogin && (
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-primary hover:text-primary/80 transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>

              {/* Confirm Password - Only show on registration */}
              {!isLogin && (
                <div>
                  <label className="form-label">Confirm Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      className={`form-input pl-12 pr-12 ${formData.confirmPassword && !passwordValidation.passwordsMatch ? 'border-red-500 focus:border-red-500' : formData.confirmPassword && passwordValidation.passwordsMatch ? 'border-green-500 focus:border-green-500' : ''}`}
                      required
                      data-testid="auth-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="toggle-confirm-password-btn"
                      title={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {formData.confirmPassword && (
                    <p className={`mt-1.5 text-xs flex items-center gap-1 ${passwordValidation.passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                      {passwordValidation.passwordsMatch ? (
                        <><CheckCircle2 className="w-3.5 h-3.5" /> Passwords match</>
                      ) : (
                        <><X className="w-3.5 h-3.5" /> Passwords do not match</>
                      )}
                    </p>
                  )}
                </div>
              )}

              {!isLogin && (
                <div className="pt-4">
                  <div>
                    <label className="form-label">{t('phone')} (Optional)</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+599 9 123 4567"
                        className="form-input pl-12"
                        data-testid="auth-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">{t('location')} (Optional)</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                      <SafariDropdown
                        value={formData.location}
                        onChange={(val) => setFormData(prev => ({ ...prev, location: val }))}
                        options={[
                          { value: '', label: 'Select your island...' },
                          ...islands.map(island => ({
                            value: island.name,
                            label: `${island.flag || '🏝️'} ${island.name}`
                          }))
                        ]}
                        placeholder="Select your island..."
                        className="w-full [&_button]:pl-12"
                        testId="auth-location-select"
                      />
                    </div>
                  </div>

                  {/* Preferences */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="form-label">{t('preferred_language')}</label>
                      <div className="relative">
                        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                        <SafariDropdown
                          value={formData.preferred_language}
                          onChange={(val) => setFormData(prev => ({ ...prev, preferred_language: val }))}
                          options={[
                            { value: 'en', label: 'English' },
                            { value: 'nl', label: 'Nederlands' },
                            { value: 'es', label: 'Español' }
                          ]}
                          placeholder={t('preferred_language')}
                          className="[&_button]:pl-12"
                          testId="auth-language-select"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="form-label">{t('currency')}</label>
                      <div className="relative">
                        <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                        <SafariDropdown
                          value={formData.preferred_currency}
                          onChange={(val) => setFormData(prev => ({ ...prev, preferred_currency: val }))}
                          options={[
                            { value: 'USD', label: 'USD ($)' },
                            { value: 'XCG', label: 'XCG' }
                          ]}
                          placeholder={t('currency')}
                          className="[&_button]:pl-12"
                          testId="auth-currency-select"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Terms & Conditions Checkbox - Only show on registration */}
              {!isLogin && (
                <div 
                  className="flex items-start gap-3 p-4 mt-2 bg-secondary/50 rounded-xl border border-border cursor-pointer"
                  onClick={() => setAcceptedTerms(!acceptedTerms)}
                >
                  <div 
                    className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      acceptedTerms 
                        ? 'bg-primary border-primary' 
                        : 'bg-transparent border-muted-foreground/50'
                    }`}
                    data-testid="accept-terms-checkbox"
                  >
                    {acceptedTerms && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground leading-relaxed select-none">
                    {language === 'nl' ? (
                      <>
                        Ik heb de{' '}
                        <a 
                          href="/terms" 
                          target="_blank" 
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Algemene Voorwaarden
                        </a>{' '}
                        gelezen en ga hiermee akkoord.
                      </>
                    ) : language === 'es' ? (
                      <>
                        He leído y acepto los{' '}
                        <a 
                          href="/terms" 
                          target="_blank" 
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Términos y Condiciones
                        </a>
                        .
                      </>
                    ) : (
                      <>
                        I have read and agree to the{' '}
                        <a 
                          href="/terms" 
                          target="_blank" 
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms & Conditions
                        </a>
                        .
                      </>
                    )}
                  </span>
                </div>
              )}

              {/* Turnstile CAPTCHA (Register only) */}
              {!isLogin && TURNSTILE_SITE_KEY && (
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onError={() => {
                      setTurnstileToken(null);
                      toast.error('CAPTCHA verification failed. Please try again.');
                    }}
                    onExpire={() => setTurnstileToken(null)}
                    options={{
                      theme: 'auto',
                      size: 'normal'
                    }}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading || uploadingDocs || (!isLogin && TURNSTILE_SITE_KEY && !turnstileToken)}
                className="w-full py-3.5 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-opacity disabled:opacity-50 btn-press flex items-center justify-center gap-2"
                data-testid="auth-submit-btn"
              >
                {loading || uploadingDocs ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {uploadingDocs ? 'Uploading documents...' : isLogin ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  isLogin ? t('sign_in') : t('sign_up')
                )}
              </button>
            </form>
            )}

            {/* Social Login Divider */}
            {!registrationSuccess && (socialConfig.google_enabled || socialConfig.facebook_enabled || socialConfig.apple_enabled) && (
              <>
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-card text-muted-foreground">or continue with</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Google Login Button */}
                  {socialConfig.google_enabled && (
                    <button
                      type="button"
                      onClick={handleGoogleLogin}
                      disabled={googleLoading || facebookLoading || appleLoading}
                      className="w-full py-3.5 bg-white dark:bg-slate-800 border border-border rounded-full font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
                      data-testid="google-login-btn"
                    >
                      {googleLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <GoogleIcon className="w-5 h-5" />
                          <span className="text-foreground">Continue with Google</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* Facebook Login Button */}
                  {socialConfig.facebook_enabled && (
                    <button
                      type="button"
                      onClick={handleFacebookLogin}
                      disabled={googleLoading || facebookLoading || appleLoading}
                      className="w-full py-3.5 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-full font-medium transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
                      data-testid="facebook-login-btn"
                    >
                      {facebookLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <FacebookIcon className="w-5 h-5" />
                          <span>Continue with Facebook</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* Apple Login Button */}
                  {socialConfig.apple_enabled && (
                    <button
                      type="button"
                      onClick={handleAppleLogin}
                      disabled={googleLoading || facebookLoading || appleLoading}
                      className="w-full py-3.5 bg-black hover:bg-gray-900 text-white rounded-full font-medium transition-colors flex items-center justify-center gap-3 disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-gray-100"
                      data-testid="apple-login-btn"
                    >
                      {appleLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <AppleIcon className="w-5 h-5" />
                          <span>Continue with Apple</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Toggle */}
            {!registrationSuccess && (
            <div className="mt-6 text-center">
              <p className="text-muted-foreground">
                {isLogin ? t('dont_have_account') : t('already_have_account')}
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="ml-2 text-primary font-medium hover:opacity-80 transition-opacity"
                  data-testid="toggle-auth-mode-btn"
                >
                  {isLogin ? t('sign_up') : t('sign_in')}
                </button>
              </p>
            </div>
            )}
          </div>

          {/* Business Info */}
          {!isLogin && !registrationSuccess && accountType === 'business' && (
            <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/20">
              <h4 className="font-medium text-foreground mb-2">Business Account Benefits</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Get a verified business badge</li>
                <li>• Display your business name on listings</li>
                <li>• Add OB (tax) pricing to your items</li>
                <li>• Build trust with customers</li>
              </ul>
            </div>
          )}

          {/* Trust Badges - USPs */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">Trusted by island communities</p>
            <div className="flex justify-center gap-6">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-1.5 rounded-full bg-primary/10 flex items-center justify-center">
                  <Palmtree className="w-5 h-5 text-primary" />
                </div>
                <div className="text-sm font-semibold text-foreground">Local Focus</div>
                <div className="text-xs text-muted-foreground">Dichtbij jou</div>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-1.5 rounded-full bg-primary/10 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                </div>
                <div className="text-sm font-semibold text-foreground">Verified Community</div>
                <div className="text-xs text-muted-foreground">Veilig handelen</div>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-1.5 rounded-full bg-primary/10 flex items-center justify-center">
                  <Tag className="w-5 h-5 text-primary" />
                </div>
                <div className="text-sm font-semibold text-foreground">100% Free</div>
                <div className="text-xs text-muted-foreground">Geen kosten</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </div>
  );
};

export default AuthPage;
